/**
 * ventaDiscountReplay.ts — Reconstruye DiscountBreakdown desde venta persistida.
 *
 * Usado por ticket preview, térmica e historial cuando la venta ya fue cobrada.
 */

import type { Venta } from "../types/caja.types";
import type {
  DiscountBreakdown,
  DiscountBreakdownLine,
  LineAdjustment,
  LineDiscount,
} from "./discountHelper";
import { ventaProductoKey } from "./devolucionRefundCalculator";

const clampMoney = (value: number): number => Math.max(0, Math.round(value));

export interface VentaDescuentosBreakdownSnapshot {
  subtotalBruto: number;
  totalDescuentoItems: number;
  descuentoGlobal: number;
  globalDiscount?: LineDiscount | null;
  lines: Array<{
    lineId: string;
    nombre: string;
    bruto: number;
    netoAntesGlobal: number;
    descuentoItem: number;
    netoItem: number;
    descuentoGlobalAsignado: number;
    descuentoTotalLinea: number;
    precioUnitarioOriginal?: number;
    precioUnitarioFinal?: number;
    cantidad?: number;
    ajusteAplicado?: LineAdjustment | null;
  }>;
}

function findLineDetail(venta: Venta, producto: Venta["productos"][number]) {
  const key = ventaProductoKey(producto);
  return venta.descuentos?.lineDetails?.find(
    (d) =>
      ventaProductoKey({
        productoId: d.productoId,
        variantKey: d.variantKey,
        modifierKey: d.modifierKey,
      }) === key,
  );
}

function inferAjuste(
  bruto: number,
  netoAntesGlobal: number,
  unitOrig: number,
  unitFinal: number,
): LineAdjustment | null {
  const descuentoItem = Math.max(0, bruto - netoAntesGlobal);
  if (descuentoItem <= 0) return null;
  if (unitOrig > 0 && unitFinal > 0 && unitOrig !== unitFinal) {
    const pct = Math.round((1 - unitFinal / unitOrig) * 100);
    if (pct > 0) {
      return { mode: "discountPercent", discountPercent: pct };
    }
  }
  return { mode: "discountPercent", discountPercent: 0 };
}

/** Reconstruye breakdown desde snapshot completo o lineDetails (ventas antiguas). */
export function rebuildDiscountBreakdownFromVenta(
  venta: Venta,
): DiscountBreakdown | undefined {
  const d = venta.descuentos;
  if (!d || (d.totalDescuentoItems <= 0 && d.descuentoGlobal <= 0)) {
    return undefined;
  }

  const snap = d.breakdownSnapshot;
  if (snap?.lines?.length) {
    const lines: DiscountBreakdownLine[] = snap.lines.map((line) => ({
      lineId: line.lineId,
      nombre: line.nombre,
      bruto: line.bruto,
      netoAntesGlobal: line.netoAntesGlobal,
      descuentoItem: line.descuentoItem,
      netoItem: line.netoItem,
      descuentoGlobalAsignado: line.descuentoGlobalAsignado,
      descuentoTotalLinea: line.descuentoTotalLinea,
      ajusteAplicado: line.ajusteAplicado ?? null,
      precioUnitarioOriginal: line.precioUnitarioOriginal,
      precioUnitarioFinal: line.precioUnitarioFinal,
      cantidad: line.cantidad,
    }));
    return {
      subtotalBruto: snap.subtotalBruto,
      totalDescuentoItems: snap.totalDescuentoItems,
      descuentoGlobal: snap.descuentoGlobal,
      total:
        snap.subtotalBruto - snap.totalDescuentoItems - snap.descuentoGlobal,
      lines,
    };
  }

  const lines: DiscountBreakdownLine[] = venta.productos.map((p, index) => {
    const detail = findLineDetail(venta, p);
    const qty = Math.max(1, detail?.cantidad ?? p.cantidad);
    const unitOrig =
      detail?.precioUnitarioOriginal ??
      (detail?.bruto && qty > 0
        ? clampMoney(detail.bruto / qty)
        : p.precioUnitario);
    const bruto = detail?.bruto ?? clampMoney(unitOrig * qty);
    const netoFinal = detail?.netoFinal ?? bruto;

    const lineAdj = d.lineAdjustments?.find((la) => la.nombre === p.nombre);
    const descuentoFromAdj = lineAdj?.descuentoTotalLinea ?? 0;

    let netoAntesGlobal = bruto;
    let descuentoItem = 0;

    if (
      detail?.precioUnitarioFinal != null &&
      detail.precioUnitarioOriginal != null
    ) {
      const unitAfterItem = detail.precioUnitarioFinal;
      const itemNet = clampMoney(unitAfterItem * qty);
      if (itemNet <= bruto) {
        netoAntesGlobal = itemNet;
        descuentoItem = Math.max(0, bruto - itemNet);
      }
    }

    if (descuentoItem <= 0 && descuentoFromAdj > 0) {
      descuentoItem = Math.min(descuentoFromAdj, bruto);
      netoAntesGlobal = Math.max(0, bruto - descuentoItem);
    }

    const descuentoGlobalAsignado = Math.max(0, netoAntesGlobal - netoFinal);
    const unitAfterItem =
      qty > 0 ? clampMoney(netoAntesGlobal / qty) : unitOrig;

    return {
      lineId: `${p.productoId}#${index}`,
      nombre: p.nombre,
      bruto,
      netoAntesGlobal,
      descuentoItem,
      netoItem: netoFinal,
      descuentoGlobalAsignado,
      descuentoTotalLinea: Math.max(0, bruto - netoFinal),
      ajusteAplicado: inferAjuste(
        bruto,
        netoAntesGlobal,
        unitOrig,
        unitAfterItem,
      ),
      precioUnitarioOriginal: unitOrig,
      precioUnitarioFinal: unitAfterItem,
      cantidad: qty,
    };
  });

  const subtotalBruto = lines.reduce((s, l) => s + l.bruto, 0);
  const totalDescuentoItems = lines.reduce((s, l) => s + l.descuentoItem, 0);
  const descuentoGlobal =
    d.descuentoGlobal > 0
      ? d.descuentoGlobal
      : lines.reduce((s, l) => s + l.descuentoGlobalAsignado, 0);

  return {
    subtotalBruto,
    totalDescuentoItems,
    descuentoGlobal,
    total: subtotalBruto - totalDescuentoItems - descuentoGlobal,
    lines,
  };
}

export function rebuildGlobalDiscountFromVenta(
  venta: Venta,
): LineDiscount | null {
  const snap = venta.descuentos?.breakdownSnapshot?.globalDiscount;
  if (snap && snap.value > 0) return snap;
  const pct = venta.descuentos?.globalDiscountPct;
  if (pct != null && pct > 0) return { type: "%", value: pct };
  const global = venta.descuentos?.descuentoGlobal ?? 0;
  if (global > 0) return { type: "$", value: global };
  return null;
}

/** Precio de lista para ticket (catálogo), no el neto persistido. */
export function ventaProductoListPrice(
  venta: Venta,
  producto: Venta["productos"][number],
): number {
  const detail = findLineDetail(venta, producto);
  return detail?.precioUnitarioOriginal ?? producto.precioUnitario;
}
